<template>
  <div class="dashboard">
    <div class="row mb-4">
      <div class="col-md-12 d-flex justify-content-between align-items-center">
        <h1>Dashboard Annuale</h1>
        <div class="d-flex align-items-center">
          <label for="yearSelect" class="me-2 fw-bold">Anno:</label>
          <select id="yearSelect" class="form-select w-auto" v-model="selectedYear" @change="fetchData">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
          <button class="btn btn-primary ms-3" @click="fetchData" :disabled="loading">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> Aggiorna
          </button>
        </div>
      </div>
    </div>

    <ApiLoader ref="apiLoader">
      <template #error>
        <div class="alert alert-danger">
          Si è verificato un errore nel caricamento dei dati della dashboard.
        </div>
      </template>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Caricamento...</span>
        </div>
      </div>

      <div v-else>

        <!-- Sezione Progetti -->
        <div>
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0"><i class="fas fa-project-diagram me-2 text-primary"></i>Rendicontazione Progetti {{ selectedYear }}</h5>
          </div>
          
          <TableSortable :data="projectsData" :options="projectsTableOptions">
            <template #field-project_name="{ row }">
              <router-link :to="`/projects/${row.id_project}`" class="text-decoration-none">
                {{ row.project_name }}
              </router-link>
            </template>
            
            <template #field-budget_annuale="{ row }">
              {{ formatCurrency(row.budget_annuale) }}
            </template>
            
            <template #field-budget_previsto="{ row }">
              {{ formatCurrency(row.budget_previsto) }}
            </template>
            
            <template #field-budget_consolidato="{ row }">
              {{ formatCurrency(row.budget_consolidato) }}
            </template>
            
            <template #field-budget_fatturato="{ row }">
              {{ formatCurrency(row.budget_fatturato) }}
            </template>
            
            <template #field-budget_rendicontato="{ row }">
              <span class="fw-bold">{{ formatCurrency(row.budget_rendicontato) }}</span>
            </template>
            
            <template #field-residuo="{ row }">
              <span :class="{'text-success': row.residuo === 0, 'text-danger': row.residuo > 0, 'text-extreme-danger': row.residuo < 0}">
                {{ formatCurrency(row.residuo) }}
              </span>
            </template>
            
            <template #footer>
              <tr class="fw-bold table-active">
                <td>TOTALE</td>
                <td class="text-end">{{ formatCurrency(projectsTotals.annuale) }}</td>
                <td class="text-end">{{ formatCurrency(projectsTotals.previsto) }}</td>
                <td class="text-end">{{ formatCurrency(projectsTotals.consolidato) }}</td>
                <td class="text-end">{{ formatCurrency(projectsTotals.fatturato) }}</td>
                <td class="text-end">{{ formatCurrency(projectsTotals.rendicontato) }}</td>
                <td class="text-end" :class="{'text-success': projectsTotals.residuo === 0, 'text-danger': projectsTotals.residuo > 0, 'text-extreme-danger': projectsTotals.residuo < 0}">
                  {{ formatCurrency(projectsTotals.residuo) }}
                </td>
              </tr>
            </template>
          </TableSortable>
        </div>
        <hr/>
        <!-- Sezione Persone -->
        <div class="mb-4">
          <div class="d-flex align-items-center mb-3">
            <h5 class="mb-0"><i class="fas fa-users me-2 text-success"></i>Rendicontazione Persone {{ selectedYear }}</h5>
          </div>
          
          <TableSortable :data="peopleData" :options="peopleTableOptions">
            <template #field-person_name="{ row }">
              <router-link :to="`/people/${row.id_people}`" class="text-decoration-none">
                {{ row.person_name }}
              </router-link>
            </template>
            
            <template #field-budget_annuale="{ row }">
              {{ formatCurrency(row.budget_annuale) }}
            </template>
            
            <template #field-budget_previsto="{ row }">
              {{ formatCurrency(row.budget_previsto) }}
            </template>
            
            <template #field-budget_consolidato="{ row }">
              {{ formatCurrency(row.budget_consolidato) }}
            </template>
            
            <template #field-budget_fatturato="{ row }">
              {{ formatCurrency(row.budget_fatturato) }}
            </template>
            
            <template #field-budget_rendicontato="{ row }">
              <span class="fw-bold">{{ formatCurrency(row.budget_rendicontato) }}</span>
            </template>
            
            <template #field-residuo="{ row }">
              <span :class="{'text-success': row.residuo === 0, 'text-danger': row.residuo > 0, 'text-extreme-danger': row.residuo < 0}">
                {{ formatCurrency(row.residuo) }}
              </span>
            </template>
            
            <template #footer>
              <tr class="fw-bold table-active">
                <td>TOTALE</td>
                <td class="text-end">{{ formatCurrency(peopleTotals.annuale) }}</td>
                <td class="text-end">{{ formatCurrency(peopleTotals.previsto) }}</td>
                <td class="text-end">{{ formatCurrency(peopleTotals.consolidato) }}</td>
                <td class="text-end">{{ formatCurrency(peopleTotals.fatturato) }}</td>
                <td class="text-end">{{ formatCurrency(peopleTotals.rendicontato) }}</td>
                <td class="text-end" :class="{'text-success': peopleTotals.residuo === 0, 'text-danger': peopleTotals.residuo > 0, 'text-extreme-danger': peopleTotals.residuo < 0}">
                  {{ formatCurrency(peopleTotals.residuo) }}
                </td>
              </tr>
            </template>
          </TableSortable>
        </div>

        
      </div>
    </ApiLoader>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAppStore } from '../stores/app.js'
import ApiLoader from '../lib/ApiLoader.vue'
import TableSortable from '../components/TableSortable.vue'

const store = useAppStore()
const loading = ref(false)
const selectedYear = ref(new Date().getFullYear())
const peopleData = ref([])
const projectsData = ref([])

// Genera lista anni dal 2020 al 2030
const availableYears = computed(() => {
  const years = []
  const currentYear = new Date().getFullYear()
  for (let y = 2020; y <= currentYear + 5; y++) {
    years.push(y)
  }
  return years.sort((a, b) => b - a) // Ordine decrescente
})

// Calcola totali per footer tabelle
const peopleTotals = computed(() => {
    return peopleData.value.reduce((acc, curr) => {
        acc.annuale += curr.budget_annuale || 0;
        acc.previsto += curr.budget_previsto || 0;
        acc.consolidato += curr.budget_consolidato || 0;
        acc.fatturato += curr.budget_fatturato || 0;
        acc.rendicontato += curr.budget_rendicontato || 0;
        acc.residuo += curr.residuo || 0;
        return acc;
    }, { annuale: 0, previsto: 0, consolidato: 0, fatturato: 0, rendicontato: 0, residuo: 0 });
});

const projectsTotals = computed(() => {
    return projectsData.value.reduce((acc, curr) => {
        acc.annuale += curr.budget_annuale || 0;
        acc.previsto += curr.budget_previsto || 0;
        acc.consolidato += curr.budget_consolidato || 0;
        acc.fatturato += curr.budget_fatturato || 0;
        acc.rendicontato += curr.budget_rendicontato || 0;
        acc.residuo += curr.residuo || 0;
        return acc;
    }, { annuale: 0, previsto: 0, consolidato: 0, fatturato: 0, rendicontato: 0, residuo: 0 });
});

// Definizione struttura tabella persone
const peopleTableOptions = computed(() => ({
  structure: [
    { key: 'person_name', label: 'Persona', sortable: true, filterable: true },
    { key: 'budget_annuale', label: 'Budget Annuale', sortable: true, align: 'right' },
    { key: 'budget_previsto', label: 'Previsto', sortable: true, align: 'right' },
    { key: 'budget_consolidato', label: 'Consolidato', sortable: true, align: 'right' },
    { key: 'budget_fatturato', label: 'Fatturato', sortable: true, align: 'right' },
    { key: 'budget_rendicontato', label: 'Totale Rendicontato', sortable: true, align: 'right' },
    { key: 'residuo', label: 'Residuo', sortable: true, align: 'right' }
  ]
}))

// Definizione struttura tabella progetti
const projectsTableOptions = computed(() => ({
  structure: [
    { key: 'project_name', label: 'Progetto', sortable: true, filterable: true },
    { key: 'budget_annuale', label: 'Budget Annuale', sortable: true, align: 'right' },
    { key: 'budget_previsto', label: 'Previsto', sortable: true, align: 'right' },
    { key: 'budget_consolidato', label: 'Consolidato', sortable: true, align: 'right' },
    { key: 'budget_fatturato', label: 'Fatturato', sortable: true, align: 'right' },
    { key: 'budget_rendicontato', label: 'Totale Rendicontato', sortable: true, align: 'right' },
    { key: 'residuo', label: 'Residuo', sortable: true, align: 'right' }
  ]
}))



const formatCurrency = (value) => {
  if (value === undefined || value === null) return '0,00 €';
  return parseFloat(value).toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  });
}

const fetchData = async () => {
  loading.value = true
  try {
    // Usa direttamente fetch o axios se store.apiStore è legato a ApiLoader in modo specifico
    // Qui uso la logica custom per chiamare l'API e popolare i dati locali
    // Se ApiLoader si aspetta che i dati siano nello store, potrei dover adattare.
    // Ma vedo che ApiLoader usa store.apiResult.
    
    // Provo a usare store.apiStore che sembra fare la chiamata e mettere il risultato in store.apiResult
    // Ma attenzione: se uso ApiLoader, esso reagisce a store.apiResult.
    // Tuttavia, voglio gestire due tabelle diverse.
    
    // Opzione migliore: chiamare l'API e gestire i dati localmente, usando ApiLoader solo come wrapper visuale o per gestire errori globali.
    // Ma ApiLoader nel codice esistente sembra molto legato allo store.
    
    // Controllo come funziona ApiLoader in Home.vue:
    // store.apiStore("/project/dashboard");
    // e poi usa store.apiResult.data.stats
    
    // Qui farò una chiamata diretta usando il token dello store se necessario, o meglio, uso una funzione di servizio se esiste.
    // Poiché non vedo un servizio generico esposto facilmente, userò fetch con i messaggi di auth dello store se possibile, 
    // oppure uso store.apiStore e leggo il risultato.
    
    await store.apiStore(`/dashboard/${selectedYear.value}`);
    
    if (store.apiResult && store.apiResult.ok) {
        peopleData.value = store.apiResult.data.people;
        projectsData.value = store.apiResult.data.projects;
    } else {
        peopleData.value = [];
        projectsData.value = [];
    }

  } catch (error) {
    console.error("Errore caricamento dashboard:", error);
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.card-header {
  font-weight: bold;
}


</style>
